// Resolution partagee de l'image d'un evenement, utilisee par le carrousel,
// la liste et la preview carte. Avant ce module, chaque surface choisissait
// `image.url` (l'original, parfois plusieurs Mo) avant `image.thumbnailUrl`
// (la miniature deja generee par le backend), et ajoutait des parametres de
// resize (`?w=...&h=...&fit=crop`) qui n'ont aucun effet sur une URL Firebase
// Storage ordinaire — cf. docs/event-menu-apple-like-performance-execution-spec.md,
// section 6, P0-IMG-01.
//
// Module pur : aucun DOM, aucun effet de bord. Ne jamais ajouter de
// parametres de redimensionnement ici, et ne jamais transformer une URL
// `blob:`/`data:`.

function isUsableUrl(value) {
  if (typeof value !== "string") return false;
  const normalizedValue = value.trim().toLowerCase();
  return Boolean(normalizedValue) && normalizedValue !== "null" && normalizedValue !== "undefined";
}

export function isInlineDataImage(url) {
  return typeof url === "string" && url.startsWith("data:");
}

function isBlobUrl(url) {
  return typeof url === "string" && url.startsWith("blob:");
}

// Un "media" peut etre une string (forme legacy) ou un objet {url, thumbnailUrl}.
// Prefere toujours thumbnailUrl, jamais de transformation sur blob:/data:.
export function resolveMediaThumbnailUrl(media) {
  if (isUsableUrl(media)) return media;
  if (!media || typeof media !== "object") return "";
  if (isUsableUrl(media.thumbnailUrl)) return media.thumbnailUrl;
  if (isUsableUrl(media.imageUrl)) return media.imageUrl;
  if (isUsableUrl(media.src)) return media.src;
  if (isUsableUrl(media.secureUrl)) return media.secureUrl;
  if (isUsableUrl(media.url)) return media.url;
  return "";
}

function resolveMediaOriginalUrl(media) {
  if (isUsableUrl(media)) return media;
  if (!media || typeof media !== "object") return "";
  if (isUsableUrl(media.url)) return media.url;
  if (isUsableUrl(media.imageUrl)) return media.imageUrl;
  if (isUsableUrl(media.src)) return media.src;
  if (isUsableUrl(media.secureUrl)) return media.secureUrl;
  return "";
}

function resolvePrimaryImage(images = []) {
  if (!Array.isArray(images) || images.length === 0) {
    return null;
  }

  return images.find((image) => image?.isPrimary) || images.find(Boolean) || null;
}

// Ordre de priorite strict: image primaire miniaturee, champ plat miniature,
// puis fallbacks legacy/originaux quand la source detail/agenda ne fournit pas
// encore de miniature.
export function getEventThumbnailUrl(event, fallback = "") {
  const primaryImage = resolvePrimaryImage(event?.images);
  const candidates = [
    primaryImage?.thumbnailUrl,
    event?.thumbnailUrl,
    event?.image?.thumbnailUrl,
    event?.coverImage?.thumbnailUrl,
    event?.hero?.thumbnailUrl,
    resolveMediaThumbnailUrl(event?.hero?.image),
    resolveMediaOriginalUrl(primaryImage),
    resolveMediaOriginalUrl(event?.image),
    resolveMediaOriginalUrl(event?.coverImage),
    event?.coverImageUrl,
    event?.hero?.imageUrl,
    event?.imageUrl,
  ];

  for (const candidate of candidates) {
    if (isUsableUrl(candidate)) return candidate;
  }

  return fallback;
}

// Galerie de preview : une entree par image de event.images (miniature
// preferee), plus event.image en complement si distinct, plus event.thumbnailUrl
// (forme plate du DTO discovery — cf. phase 2 : ce DTO n'a pas de tableau
// `images`, seulement un champ `thumbnailUrl` unique) si aucune des
// precedentes n'a rien fourni. Deduplique en conservant l'ordre d'apparition.
export function getEventPreviewImageUrls(event) {
  const rawImages = Array.isArray(event?.images) ? event.images : [];
  const urls = rawImages.map((image) => resolveMediaThumbnailUrl(image)).filter(isUsableUrl);

  const singleImage = resolveMediaThumbnailUrl(event?.image);
  if (isUsableUrl(singleImage)) {
    urls.push(singleImage);
  }

  if (isUsableUrl(event?.thumbnailUrl)) {
    urls.push(event.thumbnailUrl);
  }

  const coverImage = resolveMediaThumbnailUrl(event?.coverImage);
  if (isUsableUrl(coverImage)) {
    urls.push(coverImage);
  }

  if (isUsableUrl(event?.coverImageUrl)) {
    urls.push(event.coverImageUrl);
  }

  const seen = new Set();
  const deduped = [];
  for (const url of urls) {
    if (seen.has(url)) continue;
    seen.add(url);
    deduped.push(url);
  }
  return deduped;
}

export { isBlobUrl };
